import { html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { PageElement } from "../lib/page-element.js";
import { shared, authStyles } from "../styles.js";
import { signup, requestOtp, authWithOtp } from "../api/account.js";
import { parseError, errorMessage, humanError } from "../api/errors.js";
import { navigate } from "../nav.js";

/**
 * page-signup is passwordless: enter an email and accept the terms, then confirm
 * the emailed one-time code — which verifies the account and signs in. There is
 * no password and no handle here; a freshly verified account has an empty handle,
 * so the router routes it to /welcome to pick one. Signup creates the record
 * (POST /api/registry/signup); the OTP step reuses the same native flow as login.
 */
@customElement("page-signup")
export class PageSignup extends PageElement {
  static override styles = [
    shared,
    authStyles,
    css`
      .consent {
        display: flex;
        gap: 8px;
        align-items: flex-start;
        margin: var(--sp-3) 0 var(--sp-4);
      }
      .consent input {
        margin-top: 3px;
        width: auto;
      }
    `,
  ];

  @state() private email = "";
  @state() private accepted = false;
  @state() private code = "";
  @state() private otpId = "";
  @state() private step: "form" | "code" = "form";
  @state() private errorMsg = "";
  @state() private submitting = false;

  override firstUpdated() {
    this.setTitle("Create account");
    super.firstUpdated();
  }

  private async submit(e: Event) {
    e.preventDefault();
    this.errorMsg = "";
    if (!this.accepted) {
      this.errorMsg = "Please accept the terms and privacy policy to continue.";
      return;
    }
    this.submitting = true;
    try {
      const email = this.email.trim();
      await signup({ email, termsAccepted: true });
      this.otpId = await requestOtp(email);
      this.code = "";
      this.step = "code";
    } catch (err) {
      const { code } = parseError(err);
      // A taken email answers 409 name_taken; steer the user to sign in.
      this.errorMsg =
        code === "name_taken"
          ? "That email is already registered. Sign in instead."
          : humanError(err);
    } finally {
      this.submitting = false;
    }
  }

  private async submitCode(e: Event) {
    e.preventDefault();
    this.errorMsg = "";
    this.submitting = true;
    try {
      await authWithOtp(this.otpId, this.code.trim());
      // New account has no handle yet → the router guard routes to /welcome.
      const next = new URLSearchParams(location.search).get("next") || "/";
      navigate(next, { replace: true });
    } catch (err) {
      this.errorMsg =
        parseError(err).code === "rate_limited"
          ? errorMessage("rate_limited")
          : "That code is not valid or has expired. Request a new one.";
    } finally {
      this.submitting = false;
    }
  }

  private async resend() {
    if (this.submitting) return;
    this.errorMsg = "";
    this.submitting = true;
    try {
      // The account already exists after submit(); only a fresh code is needed.
      this.otpId = await requestOtp(this.email.trim());
      this.code = "";
    } catch (err) {
      this.errorMsg =
        parseError(err).code === "rate_limited" ? errorMessage("rate_limited") : humanError(err);
    } finally {
      this.submitting = false;
    }
  }

  override render() {
    return html`
      <div class="auth-card">
        <div class="mark">
          <span class="glyph" aria-hidden="true">b</span>
          <span class="word">bino Registry</span>
        </div>
        ${this.errorMsg ? html`<div class="error" role="alert">${this.errorMsg}</div>` : ""}
        ${this.step === "form" ? this.renderForm() : this.renderCode()}
      </div>
    `;
  }

  private renderForm() {
    return html`
      <form @submit=${this.submit}>
        <h1>Create account</h1>
        <p class="lede">
          Enter your email and we'll send you a one-time code to verify it. No password needed.
        </p>
        <div class="field">
          <label for="email">Email</label>
          <input
            id="email"
            type="email"
            autocomplete="username"
            placeholder="you@company.com"
            .value=${this.email}
            @input=${(e: Event) => (this.email = (e.target as HTMLInputElement).value)}
            required
          />
        </div>
        <label class="consent">
          <input
            type="checkbox"
            .checked=${this.accepted}
            @change=${(e: Event) => (this.accepted = (e.target as HTMLInputElement).checked)}
          />
          <span class="muted"
            >I accept the <strong>Terms of Service</strong> and the
            <strong>Privacy Policy</strong> (data protection).</span
          >
        </label>
        <div class="actions">
          <button class="btn btn-primary" type="submit" ?disabled=${this.submitting}>
            ${this.submitting ? "Sending code…" : "Create account"}
          </button>
        </div>
        <p class="alt">
          Already have an account? <a href=${"/login" + location.search}>Sign in</a>
        </p>
      </form>
    `;
  }

  private renderCode() {
    return html`
      <form @submit=${this.submitCode}>
        <h1>Check your email</h1>
        <p class="lede">
          We sent a code to <b>${this.email}</b>. Enter it to verify your account and sign in.
        </p>
        <div class="field">
          <label for="otp">Code</label>
          <input
            id="otp"
            class="code-input"
            inputmode="numeric"
            autocomplete="one-time-code"
            placeholder="123456"
            .value=${this.code}
            @input=${(e: Event) => (this.code = (e.target as HTMLInputElement).value)}
            required
          />
        </div>
        <div class="actions">
          <button class="btn btn-primary" type="submit" ?disabled=${this.submitting}>
            ${this.submitting ? "Verifying…" : "Verify & continue"}
          </button>
        </div>
        <div class="alt">
          <button type="button" ?disabled=${this.submitting} @click=${this.resend}>
            Resend code</button
          >&nbsp;·&nbsp;<button type="button" @click=${() => (this.step = "form")}>
            Use a different email
          </button>
        </div>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-signup": PageSignup;
  }
}
